import { useCallback, useMemo } from 'react';

import { TOKEN_SCOPE, useServiceAppState, useUserInfoState } from '@kc-console/context';
import { type RequiredBy } from '@kc-console/core';
import { useCheckAdmin } from '@kc-console/guard';
import { InServiceNoPagePermission as NoPagePermission } from '@kc-console/shared-ui';

import { InServiceErrorLayout } from '../ErrorLayout/InServiceErrorLayout';
import ExternalLinkButton from './components/ExternalLinkButton';
import MyRoleGuideLinkButton from './components/MyRoleGuideLinkButton';
import RoleGuideLinkButton from './components/RoleGuideLinkButton';

import type { PropsWithChildren } from 'react';
import type { ResponseCloudRole } from '@kc-console/context';
import type { InServiceNoPagePermissionProps as NoPagePermissionProps } from '@kc-console/shared-ui';
import type { ExternalLinkButtonprops } from './components/ExternalLinkButton';

export interface PageRoleBoundaryProps extends Omit<NoPagePermissionProps, 'scope'> {
  allowedDomainRoleNames?: string[];
  allowedProjectRoleNames?: string[];
  usefulLinks?: ExternalLinkButtonprops[];
}

/**
 * 페이지 접근 권한을 검사하는 경계 컴포넌트
 * @description
 * - 사용자의 도메인/프로젝트 역할을 기반으로 페이지 접근 권한을 검증합니다.
 * - 권한이 없는 경우 접근 불가 안내 페이지를 표시합니다.
 * - 유용한 링크와 가이드 정보를 함께 제공합니다.
 * - URL의 scope ID(domainId/projectId)와 현재 토큰의 scope ID가 일치하지 않으면 역할 검사 전에 차단합니다.
 *   SSR에서 잘못된 scope ID로 토큰 발급에 실패한 경우, 클라이언트가 기존 유효한 토큰으로 재조회하더라도
 *   ID 불일치로 인해 권한없음 화면을 유지합니다.
 * @example
 * // 프로젝트 관리자만 접근 가능한 페이지
 * <PageRoleBoundary allowedProjectRoleNames={['admin']}>
 *   <ProtectedContent />
 * </PageRoleBoundary>
 * @example
 * const PROJECT_UserList = (props: PageComponentProps) => {
 *   return (
 *     <PageRoleBoundary allowedProjectRoleNames={['admin', 'identity-admin']}>
 *       <UserListLayout>
 *         <UserListContent />
 *       </UserListLayout>
 *     </PageRoleBoundary>
 *   );
 * };
 */
export default function PageRoleBoundary({
  allowedDomainRoleNames = [],
  allowedProjectRoleNames = [],
  usefulLinks = [],
  showIamGuide: showIamGuideProps,
  children,
}: PropsWithChildren<PageRoleBoundaryProps>) {
  const { data: userInfo, refetch: refetchUserInfo, isRefetching } = useUserInfoState();

  const {
    state: { scope, domainId, projectId },
  } = useServiceAppState();

  const { isAdmin } = useCheckAdmin();

  const isIdMatched = useMemo(() => {
    if (scope === TOKEN_SCOPE.DOMAIN) {
      return domainId === userInfo?.domain?.id;
    } else if (scope === TOKEN_SCOPE.PROJECT) {
      return projectId === userInfo?.project?.id;
    }

    return true;
  }, [domainId, projectId, scope, userInfo?.domain?.id, userInfo?.project?.id]);

  const userRoleNames: string[] = useMemo(() => {
    const roles = userInfo?.roles ?? [];
    const reDefineRoles = (
      roles.filter(role => !!role?.name) as RequiredBy<ResponseCloudRole, 'name'>[]
    ).map(role => ({
      ...role,
      name: role.name.toLowerCase(),
    }));
    return reDefineRoles.map(role => role.name);
  }, [userInfo?.roles]);

  const showIamGuide = useMemo(() => {
    if (showIamGuideProps !== undefined) {
      return showIamGuideProps;
    } else if (scope === TOKEN_SCOPE.DOMAIN) {
      return isAdmin;
    } else if (scope === TOKEN_SCOPE.PROJECT) {
      return true;
    }

    return false;
  }, [isAdmin, scope, showIamGuideProps]);

  const hasPermission = useMemo(() => {
    if (!isIdMatched) return false;

    if (scope === TOKEN_SCOPE.PROJECT && allowedProjectRoleNames.length > 0) {
      return userRoleNames.some(roleName => allowedProjectRoleNames.includes(roleName));
    }

    if (scope === TOKEN_SCOPE.DOMAIN && allowedDomainRoleNames.length > 0) {
      return userRoleNames.some(roleName => allowedDomainRoleNames.includes(roleName));
    }

    return true;
  }, [allowedDomainRoleNames, allowedProjectRoleNames, isIdMatched, scope, userRoleNames]);

  const handleRefresh = useCallback(() => {
    if (!isIdMatched) return window.location.reload();
    refetchUserInfo();
  }, [isIdMatched, refetchUserInfo]);

  const usefulInfoActions = (
    <>
      {usefulLinks.map((usefulLink, idx) => (
        <ExternalLinkButton key={idx} {...usefulLink} />
      ))}
      <MyRoleGuideLinkButton />
      <RoleGuideLinkButton />
    </>
  );

  if (!hasPermission) {
    return (
      <InServiceErrorLayout usefulInfoProps={{ actions: usefulInfoActions }}>
        <NoPagePermission
          showIamGuide={showIamGuide}
          scope={scope}
          onRefresh={handleRefresh}
          isRefreshing={isRefetching}
        />
      </InServiceErrorLayout>
    );
  }

  return children;
}
